import { QueryClient } from '@tanstack/react-query'
import { ResultMismatchError } from './client'

/**
 * ONE client, created at MODULE SCOPE. v2 constructs its QueryClient inside
 * the `App` render body, so every re-render made a new cache — a real bug on
 * the non-migrating list (arch §8, 005 inventory). Per-query staleness comes
 * from `stalenessFor`, so nothing is set here that would silently override it.
 *
 * THE ONE DEFAULT THIS SETS — final-review bucket 1 item 2. Until now this
 * file was a bare `new QueryClient()`, which means query-core's own defaults
 * applied verbatim: `retry` is `config.retry ?? 3`
 * (`@tanstack/query-core@5.101.4`, `build/modern/retryer.js`, the
 * `shouldRetry` line) and the delay is `Math.min(1000 * 2 ** failureCount,
 * 30000)` — so a failing query is attempted FOUR times, at +0s, +1s, +3s and
 * +7s, before the screen says anything.
 *
 * That is the right behaviour for a transport failure and the wrong
 * behaviour for a `ResultMismatchError` (`api/client.ts`), which is what a
 * 200 whose body failed its own declared `result` schema becomes. That
 * failure is DETERMINISTIC: the server is answering successfully, with the
 * wrong shape, and asking it three more times gets the same wrong shape
 * three more times. Web-pre-deploy item 7's whole point is that a drift
 * surfaces instead of rendering `undefined`; making the user wait seven
 * seconds for it to surface is the same defect one layer along.
 *
 * `failureCount < 3` reproduces query-core's numeric default EXACTLY for
 * every other error (its `typeof retry === 'number' && failureCount < retry`
 * branch is the same comparison against the same zero-based counter), so
 * this is a carve-out for one error class and not a change of policy: an
 * `ApiError` from a 500, or a `TypeError` from a dropped connection, still
 * gets its four attempts. `test/query-client.test.tsx` pins BOTH halves
 * against THIS exported client — before it existed, nothing in the suite
 * exercised this module at all (`test/render.tsx` and
 * `test/result-schema.test.tsx` each build their own client with `retry:
 * false`), so the suite measured instant failure while the shipped app took
 * seven seconds: the exact "tested behaviour is not shipped behaviour" gap
 * design §4.1 rejected a dev-only throw to avoid.
 *
 * Telemetry is unaffected either way: `api/report.ts`'s dedupe keys on
 * `kind + message`, so four attempts at the same drifting query collapse to
 * one beacon.
 *
 * `retryDelay` is deliberately NOT set. The backoff shape is query-core's
 * business and nothing here has a reason to disagree with it; the only thing
 * this app knows better is WHICH errors are worth waiting for at all.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => !(error instanceof ResultMismatchError) && failureCount < 3,
    },
  },
})
